/**
 * CTABanner Organism Component
 * Call-to-action banner with demo request form (Full Name, Email, GDPR consent)
 * Submits to POST /api/demo — sends lead notification to Slack
 */

import React, { useState } from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Button } from "@/components/atoms/Button";
import { TextInput } from "@/components/atoms/TextInput";
import type { CTABannerBlok } from "@/types";

export interface CTABannerProps {
  blok: CTABannerBlok;
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

interface FormErrors {
  fullName?: string;
  email?: string;
  consent?: string;
}

const renderHeading = (text: string) => {
  const parts = text.split(/(_aha)/);
  return parts.map((part, i) => {
    if (part === "_aha") {
      return (
        <span key={i} className="normal-case">_aha</span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

function validateForm(fullName: string, email: string, consent: boolean): FormErrors {
  const errors: FormErrors = {};
  if (!fullName.trim()) {
    errors.fullName = 'Full name is required';
  }
  if (!email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Please enter a valid email address';
  }
  if (!consent) {
    errors.consent = 'You must agree to the data processing terms';
  }
  return errors;
}

export const CTABanner: React.FC<CTABannerProps> = ({ blok }) => {
  const { heading, description, cta_button, show_email_input } = blok;
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState('');

  const isLoading = status === 'loading';
  const buttonText = cta_button?.[0]?.button_text || 'Request a free demo';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(fullName, email, consent);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setStatus('loading');
    setServerError('');

    try {
      const response = await fetch('/api/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullName, email, consent }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setServerError(err.message || 'Failed to submit. Please try again later.');
    }
  };

  return (
    <section
      {...storyblokEditable(blok)}
      className="bg-primary py-12 md:py-16 lg:py-20 xl:py-24"
    >
      <Container maxWidth="md">
        <div className="flex flex-col items-center text-center gap-5 md:gap-8">
          {/* Heading */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-archivo-black leading-none text-black uppercase whitespace-pre-line">
            {renderHeading(heading)}
          </h1>

          {description && (
            <p className="text-base md:text-xl leading-normal text-black max-w-xl md:max-w-2xl">
              {description}
            </p>
          )}

          {/* Form or CTA Buttons */}
          {show_email_input ? (
            status === 'success' ? (
              <div className="flex flex-col items-center gap-4 w-full max-w-lg mt-4 md:mt-6">
                <div className="bg-black/10 border-2 border-black px-6 py-4 w-full">
                  <p className="text-black font-space-mono font-bold text-lg">
                    Thank you!
                  </p>
                  <p className="text-black/80 font-space-mono text-sm mt-1">
                    We've received your demo request and will get back to you shortly.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col items-center gap-4 md:gap-5 w-full max-w-lg mt-4 md:mt-6"
                noValidate
              >
                {/* Full Name */}
                <div className="w-full">
                  <TextInput
                    value={fullName}
                    onChange={(val) => {
                      setFullName(val);
                      if (errors.fullName) setErrors((prev) => ({ ...prev, fullName: undefined }));
                    }}
                    placeholder="Full name"
                    type="text"
                    disabled={isLoading}
                  />
                  {errors.fullName && (
                    <p className="text-black font-space-mono text-xs mt-1 text-left font-bold">
                      {errors.fullName}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="w-full">
                  <TextInput
                    value={email}
                    onChange={(val) => {
                      setEmail(val);
                      if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    placeholder="Email address"
                    type="email"
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <p className="text-black font-space-mono text-xs mt-1 text-left font-bold">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* GDPR Consent */}
                <label className="flex items-start gap-3 w-full cursor-pointer text-left">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      if (errors.consent) setErrors((prev) => ({ ...prev, consent: undefined }));
                    }}
                    disabled={isLoading}
                    className="mt-1 w-4 h-4 flex-shrink-0 accent-black"
                  />
                  <span className="text-black/80 font-space-mono text-xs leading-relaxed">
                    I agree to the processing of my personal data for the purpose of receiving product information and demo access, as described in the{" "}
                    <a href="/privacy/" target="_blank" rel="noopener" className="underline hover:text-black">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/terms/" target="_blank" rel="noopener" className="underline hover:text-black">
                      Terms
                    </a>
                    .
                  </span>
                </label>
                {errors.consent && (
                  <p className="text-black font-space-mono text-xs text-left w-full font-bold">
                    {errors.consent}
                  </p>
                )}

                {/* Server Error */}
                {serverError && (
                  <div className="bg-black/10 border-2 border-black px-4 py-2 w-full">
                    <p className="text-black font-space-mono text-xs font-bold">
                      {serverError}
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  variant={cta_button?.[0]?.button_variant || "black"}
                  size="md"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : buttonText}
                </Button>
              </form>
            )
          ) : (
            /* CTA Buttons - Large and Prominent */
            cta_button && cta_button.length > 0 && (
              <div className="flex gap-4 md:gap-6 flex-wrap justify-center mt-4 md:mt-6">
                {cta_button.map((cta, index) => (
                  <Button
                    key={index}
                    href={cta.button_link?.cached_url || cta.button_link?.url}
                    variant={cta.button_variant || "black"}
                    size="md"
                    className="border-0 font-bold"
                  >
                    {cta.button_text}
                  </Button>
                ))}
              </div>
            )
          )}
        </div>
      </Container>
    </section>
  );
};

export default CTABanner;
